/** 비교 결과 사이드바: 원본 IXD 목차(노트) 단위 목록 (변경 없는 목차는 흐리게) */

import { useMemo } from "react";

import { IxdWorkbookCompareResponse } from "@/apis/ixd-workbook-compare/route";
import { cn } from "@/lib/utils";

export type NoteTableRef = {
  kind: "matched" | "workbook" | "ixd";
  index: number;
};

export type NoteEntry = {
  id: string;
  groupLabel: string;
  noteLabel: string;
  tables: NoteTableRef[];
  /** 매칭됐지만 구조가 달라진 표 수 */
  changedCount: number;
  /** IXD에만 있는 표 수 (표 추가) */
  addedCount: number;
  /** Workbook에만 있는 표 수 (표 삭제) */
  removedCount: number;
  /** 마이그레이션으로 매칭된 표 수 — 트리는 같아도 IXD 파일 반영이 필요해 검토 대상 */
  migrationCount: number;
};

export const noteHasChanges = (note: NoteEntry) =>
  note.changedCount +
    note.addedCount +
    note.removedCount +
    note.migrationCount >
  0;

/** 노트 id가 실제 PL key(RolePrefix)인지 — "misc"/"workbook-only" 가상 노트 제외 */
export const isPlKey = (noteId: string) => /^[A-Z]/.test(noteId);

/** 원본 IXD EditorMenu 목차 순서대로 노트 단위 엔트리를 만든다.

 * workbook에만 있는 표라도 TOC 매칭으로 노트 정보가 이식된 경우 해당 노트 안에
 * 배치되고, 노트를 알 수 없는 표만 맨 뒤 "IXD 목차에 없음" 그룹으로 모인다.
 */
export const buildNoteEntries = (
  result: IxdWorkbookCompareResponse,
): NoteEntry[] => {
  const workbookOnlyWithNote = result.tablesOnlyInWorkbook
    .map((table, index) => ({
      ref: { kind: "workbook", index } as NoteTableRef,
      noteId: table.tocNoteId,
      noteLabel: table.tocNoteLabel,
      groupLabel: table.tocGroupLabel,
      tocOrder: table.tocOrder,
      changed: true,
      migration: false,
    }))
    .filter((item) => item.noteId !== null);

  const items = [
    ...result.matchedTables.map((table, index) => ({
      ref: { kind: "matched", index } as NoteTableRef,
      noteId: table.tocNoteId,
      noteLabel: table.tocNoteLabel,
      groupLabel: table.tocGroupLabel,
      tocOrder: table.tocOrder,
      changed: table.hasChanges,
      migration: table.isMigrationMatch,
    })),
    ...result.tablesOnlyInIxd.map((table, index) => ({
      ref: { kind: "ixd", index } as NoteTableRef,
      noteId: table.tocNoteId,
      noteLabel: table.tocNoteLabel,
      groupLabel: table.tocGroupLabel,
      tocOrder: table.tocOrder,
      changed: true,
      migration: false,
    })),
    ...workbookOnlyWithNote,
  ].sort((a, b) => (a.tocOrder ?? Infinity) - (b.tocOrder ?? Infinity));

  const notes = new Map<string, NoteEntry>();
  for (const item of items) {
    const id = item.noteId ?? "misc";
    let note = notes.get(id);
    if (!note) {
      note = {
        id,
        groupLabel: item.groupLabel ?? "기타",
        noteLabel: item.noteLabel ?? "목차 미지정",
        tables: [],
        changedCount: 0,
        addedCount: 0,
        removedCount: 0,
        migrationCount: 0,
      };
      notes.set(id, note);
    }
    note.tables.push(item.ref);
    if (item.ref.kind === "ixd") note.addedCount += 1;
    else if (item.ref.kind === "workbook") note.removedCount += 1;
    else if (item.changed) note.changedCount += 1;
    if (item.migration) note.migrationCount += 1;
  }

  const entries = [...notes.values()];
  const orphanWorkbookRefs = result.tablesOnlyInWorkbook
    .map((table, index) => ({ table, index }))
    .filter(({ table }) => table.tocNoteId === null)
    .map(({ index }) => ({ kind: "workbook", index }) as NoteTableRef);
  if (orphanWorkbookRefs.length > 0) {
    entries.push({
      id: "workbook-only",
      groupLabel: "Workbook",
      noteLabel: "IXD 목차에 없음",
      tables: orphanWorkbookRefs,
      changedCount: 0,
      addedCount: 0,
      removedCount: orphanWorkbookRefs.length,
      migrationCount: 0,
    });
  }
  return entries;
};

interface CompareSidebarProps {
  notes: NoteEntry[];
  activeNoteId: string | null;
  onSelect: (id: string) => void;
}

export const IxdWorkbookCompareSidebar = ({
  notes,
  activeNoteId,
  onSelect,
}: CompareSidebarProps) => {
  const changedCount = useMemo(
    () => notes.filter(noteHasChanges).length,
    [notes],
  );

  return (
    <aside className="w-80 shrink-0 border-r border-neutral-200 bg-white flex flex-col min-h-0">
      <div className="px-4 py-3 border-b border-neutral-200">
        <span className="label-1-normal-medium text-label-normal">
          목차 (변경 {changedCount}/{notes.length})
        </span>
      </div>
      <ul className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
        {notes.map((note, index) => {
          const showGroupHeader =
            index === 0 || notes[index - 1].groupLabel !== note.groupLabel;
          const changed = noteHasChanges(note);
          return (
            <li key={note.id}>
              {showGroupHeader && (
                <div className="px-2 pb-1 pt-3 first:pt-1 caption-2-regular font-medium text-label-alternative">
                  {note.groupLabel}
                </div>
              )}
              <button
                type="button"
                onClick={() => onSelect(note.id)}
                className={cn(
                  "w-full rounded-[8px] border px-3 py-2 text-left transition-colors",
                  note.id === activeNoteId
                    ? "border-primary-base bg-primary-base/8"
                    : "border-transparent hover:bg-background-neutral-neutral",
                  !changed && note.id !== activeNoteId && "opacity-60",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="body-2-normal-medium truncate text-label-normal">
                    {note.noteLabel}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {note.addedCount > 0 && (
                      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                        +{note.addedCount}
                      </span>
                    )}
                    {note.removedCount > 0 && (
                      <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-medium text-rose-700">
                        -{note.removedCount}
                      </span>
                    )}
                    {note.changedCount > 0 && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700">
                        변경 {note.changedCount}
                      </span>
                    )}
                    {note.migrationCount > 0 && (
                      <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-medium text-violet-700">
                        검토 필요 {note.migrationCount}
                      </span>
                    )}
                    {!changed && (
                      <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-500">
                        변경 없음
                      </span>
                    )}
                  </span>
                </div>
                <div className="caption-2-regular text-label-alternative">
                  {isPlKey(note.id) && (
                    <code className="mr-1.5 rounded bg-neutral-100 px-1 text-[10px] text-neutral-500">
                      {note.id}
                    </code>
                  )}
                  표 {note.tables.length}개
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};
